import { useAuth } from '@agentnet/auth';
import { DataTable, pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ColDef, ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { MarketplaceProduct } from 'api/admin/types';
import { PartyOrderInsightsProps } from 'api/file/interfaces/get-file-order-insights';
import { DocumentType, KBLinks, newWindow } from 'features/constants';
import { PARTY_VERIFICATION_SUBTITLE } from 'features/help/constants';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import PDFIcon from 'ui-kit/icons/PDF';
import { getMarketplaceOrderReport } from 'api/orderInsights/order-insights-api';
import { syncNoRowsOverlay } from 'utilities/ag-grid/grid-utility';
import { FRAUD_VALIDATION, FRAUD_VALIDATION_READ_ONLY } from 'utilities/constants/activity-rights';
import { isPastExpiry } from 'utilities/dateFormatter';
import { openDocument } from 'utilities/utilities';
import { OrderStatus, PartyRow } from './interfaces/partyList';
import { normalizePartyRows, partyVerificationColDefs } from './parties.config';
import PartyVerificationDrawer from './PartyVerificationDrawer';
import PartyVerificationEmptyState from './PartyVerificationEmptyState';
import VerificationChip from './VerificationChip';
import PartyNameRenderer from './PartyNameRenderer';
import { PartyVerificationDrawerProps } from './interfaces/PartyDrawer';

interface PdfCellRendererProps {
  data: PartyRow;
  loadingPdfFileId: string | null;
  loadingPdfOrderId: string | null;
  onClick: (fileId: string, orderId: string, partyName: string) => void;
  node: IRowNode<PartyRow>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: pxToRem(114),
    },
    contentWrap: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: theme.spacing(0, 3, 3),
      paddingBottom: 0,
    },
    spaceElements: {
      marginBottom: theme.spacing(2),
    },
    gridContainer: {
      fontFamily: theme.typography.fontFamily,
      height: 'calc(100vh - 287px) !important',
      marginTop: theme.spacing(2),
      width: '100%',
    },
    searchWrap: {
      display: 'flex',
      '& form': {
        marginBlockEnd: 'auto',
      },
    },
    contentClass: {
      margin: '0 auto auto',
    },
    searchButton: {
      height: '30px',
    },
    headerNoBottomMargin: {
      marginBottom: '0 !important',
    },
    selectedRows: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      fontSize: pxToRem(14),
      padding: theme.spacing(2, 0),
    },
  }),
);

const PartyVerification = () => {
  const { getAccessToken } = useAuth();
  const marketPlace = useMarketPlace();
  const {
    fetchPartyVerificationList,
    fetchPartyVerificationListResults,
    fetchPartyVerificationListStatus,
    fetchMarketPlaceStatus,
    fetchMarketPlaceServices,
    fetchMarketPlaceServicesResults,
    getPartyVerificationFileDetail,
  } = marketPlace ?? {};

  const gridRef = useRef<AgGridReact>(null);

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile } = profileCtx;
  const classes = useStyles();
  const [displayedRowCount, setDisplayedRowCount] = useState(0);
  const [enableStatus, setEnableStatus] = useState<PartyOrderInsightsProps['enableStatus']>({});
  const [rowData, setRowData] = useState<PartyRow[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState<PartyVerificationDrawerProps['party'] | undefined>(undefined);
  const [isNewParty, setIsNewParty] = useState<boolean>(true);
  const [loadingPdfFileId, setLoadingPdfFileId] = useState<string | null>(null);
  const [loadingPdfOrderId, setLoadingPdfOrderId] = useState<string | null>(null);
  const agentNetAdmin = profile?.activityRights?.some((right) => right.ActivityRightId === 36);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
      flex: 1,
      editable: false,
      suppressMenu: true,
    }),
    [],
  );

  const hasFraudValidationRights = useMemo(() => {
    return (
      profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION) ||
      profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION_READ_ONLY)
    );
  }, [profile?.activityRights]);

  useEffect(() => {
    fetchPartyVerificationList();
    fetchMarketPlaceServices();
  }, []);

  useEffect(() => {
    setRowData(normalizePartyRows(fetchPartyVerificationListResults ?? []));
  }, [fetchPartyVerificationListResults]);

  useEffect(() => {
    if (fetchMarketPlaceServicesResults) {
      const marketPlaceResult: PartyOrderInsightsProps['enableStatus'] = {};
      const keyMap: { [key: string]: 'contactValidation' | 'enhancedIdentityVerification' } = {
        'Contact Validation': 'contactValidation',
        'Identity Verification': 'enhancedIdentityVerification',
      };
      fetchMarketPlaceServicesResults.map((result: MarketplaceProduct) => {
        if (keyMap[result.productName])
          marketPlaceResult[keyMap[result.productName]] = {
            enabled: result.isEnabled,
            productCode: result.productCode,
            description: result.description,
          };
      });
      setEnableStatus(marketPlaceResult);
    }
  }, [fetchMarketPlaceServicesResults]);

  useEffect(() => {
    if (marketPlace.validateContactInfoStatusResults || marketPlace.enhancedIdentityVerificationStatusResults) {
      setIsNewParty(false);
    }
  }, [marketPlace.validateContactInfoStatusResults, marketPlace.enhancedIdentityVerificationStatusResults]);

  useEffect(() => {
    if (marketPlace.getPartyVerificationFileDetailResults) {
      setSelectedParty(marketPlace.getPartyVerificationFileDetailResults);
    }
  }, [marketPlace.getPartyVerificationFileDetailResults]);

  const onFilterChanged = () => {
    if (!gridRef.current?.api) return;
    syncNoRowsOverlay(gridRef.current.api);
    queueMicrotask(() => {
      if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
    });
  };

  const onModelUpdated = useCallback(() => {
    if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
  }, []);

  const handleAddNewVerification = () => {
    setDrawerOpen(true);
  };

  const handlePartyClick = async (fileId: string) => {
    setDrawerOpen(true);
    setIsNewParty(false);
    await getPartyVerificationFileDetail(fileId);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setIsNewParty(true);
    setSelectedParty(undefined);
  };

  const handlePdfClick = useCallback(
    async (fileId: string, orderId: string, partyName: string) => {
      try {
        setLoadingPdfFileId(fileId);
        setLoadingPdfOrderId(orderId);

        const token = await getAccessToken();
        const pdfResponse = await getMarketplaceOrderReport(fileId, orderId, token, true);

        const fileName = `Identity Verification ${partyName}.pdf`;
        openDocument(pdfResponse.Result.Data, DocumentType, newWindow, '', fileName);
      } finally {
        setLoadingPdfFileId(null);
        setLoadingPdfOrderId(null);
      }
    },
    [getAccessToken],
  );

  const PdfCellRenderer = useCallback((props: PdfCellRendererProps) => {
    const { data, loadingPdfFileId, loadingPdfOrderId, onClick, node } = props;
    const { fileId, idvStatus, idvDate, idvOrderId, partyName } = data;

    const isIdvComplete =
      idvStatus === OrderStatus.PASSED ||
      idvStatus === OrderStatus.FAILED ||
      idvStatus === OrderStatus.ISSUES_FOUND ||
      idvStatus === OrderStatus.WARNING;
    const isWithin90Days = idvDate ? !isPastExpiry(idvDate, 90) : false;
    const isLoadingThisPdf = loadingPdfFileId === fileId && loadingPdfOrderId === idvOrderId;

    if (!isIdvComplete || !isWithin90Days || !idvOrderId) {
      return null;
    }

    if (isLoadingThisPdf) {
      return <CircularProgress size={16} />;
    }

    return (
      <PDFIcon
        data-testid={`party-verification-link-pdf-${node.rowIndex ?? 0}`}
        fontSize="small"
        onClick={() => onClick(fileId, idvOrderId, partyName)}
        style={{ cursor: 'pointer' }}
      />
    );
  }, []);

  const pdfCellRendererParams = useMemo(
    () => ({
      loadingPdfFileId,
      loadingPdfOrderId,
      onClick: handlePdfClick,
    }),
    [loadingPdfFileId, loadingPdfOrderId, handlePdfClick],
  );

  const columnDefs = useMemo<ColDef[]>(() => {
    return partyVerificationColDefs.map((col) =>
      col.field === 'pdf' ? { ...col, cellRendererParams: pdfCellRendererParams } : col,
    );
  }, [pdfCellRendererParams]);

  const getDisplayedRowCountText = (): string => {
    const displayedCount = displayedRowCount ?? 0;
    const totalCount = fetchPartyVerificationListResults?.length ?? 0;
    return `Parties: ${displayedCount} of ${totalCount}`;
  };
  const isVerificationEnabled =
    enableStatus?.contactValidation?.enabled || enableStatus?.enhancedIdentityVerification?.enabled;

  const doPartiesExist = rowData && rowData.length > 0;

  const CustomOverlayLoading = () => {
    return (
      <div className="files-container">
        <div className="no-rows">
          <LoadingSpinner status="pending" className="files-container--spinner-root" />
        </div>
      </div>
    );
  };

  if (fetchPartyVerificationListStatus === 'pending') return <CustomOverlayLoading />;

  return (
    <ContentContainer>
      <div className={classes.contentWrap} data-testid="party-verification-page">
        <PageHeader
          disableMargin="x"
          className={doPartiesExist ? undefined : classes.headerNoBottomMargin}
          title="Party Verification"
          contentRight={
            <div className={classes.searchWrap}>
              <AgentNetButton
                onClick={handleAddNewVerification}
                color="primary"
                variant="contained"
                data-testid="party-verification-new-verification-button-top"
                size="small"
                className={classes.searchButton}
                plusIcon
                disabled={!hasFraudValidationRights}
              >
                New Verification
              </AgentNetButton>
            </div>
          }
          subtitle={PARTY_VERIFICATION_SUBTITLE}
          divider={false}
          menuItems={[{ label: 'Knowledge Base', link: KBLinks.identityVerification }]}
        />
        {doPartiesExist ? (
          <div className={`ag-theme-alpine ${classes.gridContainer}`}>
            <DataTable
              ref={gridRef}
              components={{
                verificationChip: VerificationChip,
                partyNameRenderer: (props: ICellRendererParams) => (
                  <PartyNameRenderer {...props} onPartyClick={handlePartyClick} />
                ),
                summaryRenderer: PdfCellRenderer,
              }}
              onFilterChanged={onFilterChanged}
              rowData={rowData as PartyRow[] & Record<string, unknown>[]}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection={'multiple'}
              gridOptions={{
                suppressRowClickSelection: true,
                icons: {
                  filter: '<span class="ag-icon ag-icon-filter-custom"></span>',
                },
              }}
              animateRows={true}
              onModelUpdated={onModelUpdated}
            />
          </div>
        ) : (
          <PartyVerificationEmptyState
            isVerificationEnabled={isVerificationEnabled}
            hasFraudValidationRights={hasFraudValidationRights}
            agentNetAdmin={agentNetAdmin}
            enableStatus={enableStatus}
            onAddNewVerification={handleAddNewVerification}
          />
        )}
        {doPartiesExist ? (
          <Box py={2}>
            <Typography color="textPrimary" variant="body2">
              {getDisplayedRowCountText()}
            </Typography>
          </Box>
        ) : null}
      </div>
      <PartyVerificationDrawer
        party={selectedParty}
        isNewParty={isNewParty}
        openDrawer={drawerOpen}
        closeDrawer={closeDrawer}
        enableStatus={enableStatus}
        marketPlace={marketPlace}
      />
    </ContentContainer>
  );
};

export default PartyVerification;
